import { Injectable } from '@angular/core';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class NotificationServiceService {
  private _isGeneratingToken = false;
  private _fcmToken: string | null = null;
  private _deviceId: string;
  private _deviceType: string;

  constructor(private messaging: Messaging) {
    this._deviceId = this.initDeviceId();
    this._deviceType = this.detectDeviceType();
    if (localStorage.getItem('notifications_enabled') === null) {
      localStorage.setItem('notifications_enabled', 'true');
    }
    this.listenForegroundMessages();
  }

  enableNotifications() {
    localStorage.setItem('notifications_enabled', 'true');
  }

  disableNotifications() {
    localStorage.setItem('notifications_enabled', 'false');
    if (this._fcmToken) {
      console.log('Notify backend to disable token:', this._fcmToken);
    }
  }

  async initNotificationToken(): Promise<string> {
    try {
      console.log('STEP 1 → initNotificationToken called');
      const cachedToken = sessionStorage.getItem('fcm_token');

      if (cachedToken) {
        this._fcmToken = cachedToken;
        console.log('STEP 2 → Using cached token');
        return cachedToken;
      }
      if (Notification.permission === 'default') {
        await Notification.requestPermission();
      }

      console.log(
        'STEP 3 → Browser notification permission:',
        Notification.permission,
      );
      const token = await getToken(this.messaging, {
        vapidKey: environment.vapidKey,
      });

      console.log('STEP 4 → Token Response:', token);

      if (token) {
        this._fcmToken = token;
        sessionStorage.setItem('fcm_token', token);
        return token;
      }
      console.warn('FCM token not generated. Using deviceId.');
      return this.deviceId;
    } catch (err) {
      console.error('FCM Token Error:', err);
      return this.deviceId;
    }
  }

  get isGeneratingToken(): boolean {
    return this._isGeneratingToken;
  }

  private async getFcmToken(): Promise<string | null> {
    const token = await getToken(this.messaging, {
      vapidKey: environment.vapidKey,
    });
    this._fcmToken = token;
    console.log('FCM token:', token);
    return token;
  }

  get fcmToken(): string | null {
    return this._fcmToken || sessionStorage.getItem('fcm_token');
  }

  private initDeviceId(): string {
    const key = 'deviceId';
    let id = localStorage.getItem(key);
    if (!id) {
      id = crypto.randomUUID
        ? crypto.randomUUID()
        : Date.now() + '-' + Math.random();
      localStorage.setItem(key, id);
    }
    return id;
  }

  get deviceId(): string {
    return this._deviceId;
  }

  private detectDeviceType(): string {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('windows')) return 'WEB_WINDOWS';
    if (ua.includes('mac os')) return 'WEB_MAC';
    if (ua.includes('iphone') || ua.includes('ipad')) return 'WEB_IOS';
    if (ua.includes('android')) return 'WEB_ANDROID';
    return 'WEB_OTHER';
  }

  get deviceType(): string {
    return this._deviceType;
  }

  private listenForegroundMessages() {
    onMessage(this.messaging, (payload) => {
      console.log('Foreground message received:', payload);
      const enabled = localStorage.getItem('notifications_enabled') !== 'false';

      if (!enabled) {
        console.log('Notification blocked by user preference');
        return;
      }
      const title = payload.notification?.title || 'New Notification';
      const body = payload.notification?.body || '';

      if (Notification.permission === 'granted') {
        new Notification(title, {
          body,
          requireInteraction: true,
        });
      }
    });
  }
}
